import {
    type App,
    type BonusesBalance,
    type DateRange,
    GetBalancesParams,
    type TimeBalance,
    type VacationBalance,
} from "@pentacode/core";

export type BalanceKind = "time" | "vacation" | "bonuses";

interface BalancesByKind {
    time: TimeBalance[];
    vacation: VacationBalance[];
    bonuses: BonusesBalance[];
}

const apiMethods = {
    time: "getTimeBalances",
    vacation: "getVacationBalances",
    bonuses: "getBonusesBalances",
} as const;

export const balancesKey = (kind: BalanceKind) => ["balances", kind] as const;

export function balancesQuery<K extends BalanceKind>(app: App, kind: K, range: DateRange) {
    return {
        queryKey: [...balancesKey(kind), range.from, range.to] as const,
        queryFn: ({ signal }: { signal: AbortSignal }) =>
            app.api[apiMethods[kind]](new GetBalancesParams({ ...range, resolution: "none" }), {
                signal,
            }) as Promise<BalancesByKind[K]>,
    };
}

export interface EmployeeBalancesParams extends DateRange {
    employeeId: number;
    resolution: GetBalancesParams["resolution"];
}

/** One employee's balances; the key sits under `balancesKey(kind)`, so invalidating that prefix covers it. */
export function employeeBalancesQuery<K extends BalanceKind>(
    app: App,
    kind: K,
    { employeeId, from, to, resolution }: EmployeeBalancesParams
) {
    return {
        queryKey: [...balancesKey(kind), "employee", employeeId, from, to, resolution] as const,
        queryFn: ({ signal }: { signal: AbortSignal }) =>
            app.api[apiMethods[kind]](
                new GetBalancesParams({
                    from,
                    to,
                    resolution,
                    filters: [{ type: "employeeId", value: employeeId }],
                }),
                { signal }
            ) as Promise<BalancesByKind[K]>,
    };
}
